/**
 * Hook WebSocket livreurs — écoute staff avec JWT (PR-B / PR-F).
 */

import { useEffect, useRef, useState } from 'react';
import { io } from 'socket.io-client';
import { getWebSocketBaseUrl } from '../config/api.config';
import apiService from '../services/api';

const getWebSocketUrl = () => getWebSocketBaseUrl();

function emitListen(socket, livreurId, commandeId = null) {
  const token = apiService.getAccessToken();
  if (!token) {
    return;
  }
  socket.emit('livreur:listen', { token, livreurId, commandeId });
}

export function useLivreursWebSocket(livreurId, enabled = true, commandeId = null) {
  const socketRef = useRef(null);
  const [position, setPosition] = useState(null);
  const [statut, setStatut] = useState(null);
  const [connected, setConnected] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!enabled || !livreurId) {
      return;
    }

    const socket = io(getWebSocketUrl(), {
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: 5,
    });

    socketRef.current = socket;

    socket.on('connect', () => {
      setConnected(true);
      setError(null);
      emitListen(socket, livreurId, commandeId);
    });

    socket.on('disconnect', () => {
      setConnected(false);
    });

    socket.on('connect_error', () => {
      setError('Erreur de connexion WebSocket');
      setConnected(false);
    });

    socket.on('livreur:position_updated', (data) => {
      if (data.livreurId === livreurId) {
        setPosition({
          latitude: data.latitude,
          longitude: data.longitude,
          timestamp: data.timestamp,
        });
      }
    });

    socket.on('livreur:statut_updated', (data) => {
      if (data.livreurId === livreurId) {
        setStatut(data.statut);
      }
    });

    socket.on('livreur:error', (data) => {
      setError(data.message || 'Erreur WebSocket');
    });

    return () => {
      if (socket.connected) {
        socket.emit('livreur:unlisten', { livreurId });
        socket.disconnect();
      }
    };
  }, [livreurId, enabled, commandeId]);

  return {
    position,
    statut,
    connected,
    error,
    socket: socketRef.current,
  };
}

export function useMultipleLivreursWebSocket(livreurIds = [], enabled = true) {
  const socketRef = useRef(null);
  const [positions, setPositions] = useState({});
  const [statuts, setStatuts] = useState({});
  const [connected, setConnected] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!enabled || !livreurIds || livreurIds.length === 0) {
      return;
    }

    const socket = io(getWebSocketUrl(), {
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: 5,
    });

    socketRef.current = socket;

    socket.on('connect', () => {
      setConnected(true);
      setError(null);
      livreurIds.forEach((livreurId) => {
        emitListen(socket, livreurId);
      });
    });

    socket.on('disconnect', () => {
      setConnected(false);
    });

    socket.on('connect_error', () => {
      setError('Erreur de connexion WebSocket');
      setConnected(false);
    });

    socket.on('livreur:position_updated', (data) => {
      setPositions((prev) => ({
        ...prev,
        [data.livreurId]: {
          latitude: data.latitude,
          longitude: data.longitude,
          timestamp: data.timestamp,
        },
      }));
    });

    socket.on('livreur:statut_updated', (data) => {
      setStatuts((prev) => ({
        ...prev,
        [data.livreurId]: data.statut,
      }));
    });

    socket.on('livreur:error', (data) => {
      setError(data.message || 'Erreur WebSocket');
    });

    return () => {
      if (socket.connected) {
        livreurIds.forEach((livreurId) => {
          socket.emit('livreur:unlisten', { livreurId });
        });
        socket.disconnect();
      }
    };
  }, [livreurIds.join(','), enabled]);

  return {
    positions,
    statuts,
    connected,
    error,
    socket: socketRef.current,
  };
}
